import React, { useEffect, useRef, useState } from "react";

import { useGetDataInfoRange, useGetRecentData } from "api/api-hooks/datasets-hooks";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { Tooltip } from "primereact/tooltip";
import { useResizeDetector } from "react-resize-detector";
import useChartDialogStore from "store/chart-dialog-store";
import { addToDate, getDiffSeconds, toDate, truncateDate } from "utils/time-utils";
import { getChartSizeValue } from "utils/utils";
import CalendarWrapper from "../../../basic/calendar-wrapper/CalendarWrapper";
import CustomToggleButton from "../../../basic/custom-toggle-button/CustomToggleButton";
import { dialogIcon } from "../icons";
import { DatasetChartWrapperProps } from "../interfaces";
import { defaultFromDateTime, defaultToDateTime, shouldShowTime } from "../utils";
import "./ChartWrapperStyle.scss";

export default function ChartWrapper<ResT>(props: DatasetChartWrapperProps<ResT>) {
  const [loaded, setLoaded] = useState(false);
  const { width, height, ref } = useResizeDetector();
  const { height: height2, ref: ref2 } = useResizeDetector();
  const [chartDialog, setChartDialog] = useState(false);
  const { setDateFrom, setDateTo, isOpen } = useChartDialogStore();

  const [from, setFrom] = useState(
    props.dateFrom ||
      defaultFromDateTime({
        loadConf: {
          alignUnit: "MINUTES",
          lag: 6,
          lagUnit: "HOURS",
          lead: 0,
          leadUnit: "HOURS",
          ...props.loadConf,
        },
        referenceTime: props.referenceTime,
      })
  );

  const [to, setTo] = useState(
    props.dateTo ||
      defaultToDateTime({
        loadConf: {
          alignUnit: "MINUTES",
          lag: 6,
          lagUnit: "HOURS",
          lead: 0,
          leadUnit: "HOURS",
          ...props.loadConf,
        },
        referenceTime: props.referenceTime,
      })
  );
  useEffect(() => {
    const newFrom =
      props.productItemsDateRange && props.productItemsDateRange.length > 0
        ? new Date(props.productItemsDateRange[0])
        : props.dateFrom;
    const newTo =
      props.productItemsDateRange && props.productItemsDateRange.length > 0
        ? new Date(props.productItemsDateRange[1])
        : props.dateTo;

    if (newFrom && newFrom.toISOString() !== from.toISOString()) {
      setFrom(newFrom);
    }
    if (newTo && newTo.toISOString() !== to.toISOString()) {
      setTo(newTo);
    }
  }, [props.dateFrom, props.dateTo, props.productItemsDateRange]);

  useEffect(() => {
    let dticksAmount;
    if (props.timeAxisStepSeconds) {
      dticksAmount =
        (to.getMilliseconds() - from.getMilliseconds()) / (props.timeAxisStepSeconds * 1000);
      if (dticksAmount <= props.maxDTicks!!) {
        props.setDticks!(props.timeAxisStepSeconds * 1000);
      } else {
        props.setDticks!(undefined);
      }
    }
  }, [from, to]);

  const chartDialogHandler = () => {
    if (props.chartDialogOpen) {
      return;
    }
    setChartDialog(prevState => !prevState);
  };

  const dialogButton = {
    title: "Chart in Dialog",
    name: "Chart in Dialog",
    icon: dialogIcon,
    click: chartDialogHandler,
  };

  const modeBarButtons = [...props.chartConfig?.modeBarButtonsToAdd!!];
  modeBarButtons.unshift(dialogButton);

  const config = { ...props.chartConfig, modeBarButtonsToAdd: modeBarButtons };

  const [changedTimeOffset, setChangedTimeOffset] = useState<boolean>(false);
  const [selectedTimeOffset, setSelectedTimeOffset] = useState<string | undefined>(undefined);
  const offsetOptions = [
    { label: "H/2", value: "HALFHOUR" },
    { label: "H", value: "HOURS" },
    { label: "D", value: "DAYS" },
    { label: "W", value: "WEEKS" },
    { label: "M", value: "MONTHS" },
  ];

  const requestingTime = useRef<{
    from: Date | undefined;
    to: Date | undefined;
    loading: boolean;
  }>({
    from: undefined,
    to: undefined,
    loading: false,
  });

  const [refresh, setRefresh] = useState<{
    updateFrequency: number;
    turned: boolean;
  }>({
    updateFrequency:
      (props.updateFrequency && !props.reportMode) || props.reportMode === undefined
        ? props.updateFrequency ?? 5
        : 0,
    turned: !!props.updateFrequency,
  });
  const presentDate = useRef<Date>();

  const { data: recentData, isLoading: isRecentDataLoading } = useGetRecentData(
    props.identifiers as string[],
    {
      refTime: to.toISOString(),
      options: {
        enabled: props.getMostRecent && props.showDateSelection,
        staleTime: 1000,
      },
    }
  );

  const {
    data: traces,
    isFetching: isLoading,
    refetch,
  } = useGetDataInfoRange(
    props.identifiers as string[],
    {
      uniqueKey: props.uniqueKey,
      timeStart: from.toISOString(),
      timeEnd: to.toISOString(),
      usePromiseHandler: res => {
        if (props.getMostRecent && props.showDateSelection && recentData) {
          const updatedData = res.data?.map(item => {
            const recentItem = recentData.find(recent => recent.identifier === item.identifier);
            return recentItem ? { ...item, data: recentItem.data } : item;
          });
          res.data = updatedData;
        }
        requestingTime.current.to = new Date();
        requestingTime.current.loading = false;

        if (!res.error) {
          return props.promiseHandler(res);
        }

        console.error(res.error.message);
        throw res.error;
      },
    },
    {
      enabled:
        (from && to && !props.getMostRecent && !!props.identifiers?.length) ||
        (!isRecentDataLoading && !!recentData && !!props.identifiers?.length),
      staleTime: 1000,
    }
  );

  useEffect(() => {
    if (traces && !isLoading) {
      syncpresentdate();
      setLoaded(true);
    }
    if (isLoading && !requestingTime.current.loading) {
      requestingTime.current.from = new Date();
      requestingTime.current.loading = true;
    }
  }, [traces, isLoading]);

  useEffect(() => {
    if (
      refresh.turned &&
      refresh.updateFrequency &&
      refresh.updateFrequency > 0 &&
      presentDate &&
      presentDate.current
    ) {
      const interval = setInterval(() => {
        const tempDate = truncateDate(new Date(), "MINUTES");
        if (
          presentDate &&
          presentDate.current &&
          presentDate.current
            ?.toISOString()
            .localeCompare(truncateDate(new Date(), "MINUTES").toISOString()) !== 0
        ) {
          const minutesToAdd = getDiffSeconds(tempDate, presentDate.current!) / 60;
          if (minutesToAdd > 0) {
            addTimeToBothDates(minutesToAdd);
            presentDate.current = truncateDate(new Date(), "MINUTES");
          }
        } else {
          refetch();
        }
      }, refresh.updateFrequency * 1000);
      return () => clearInterval(interval);
    }
  }, [refresh.turned, refresh.updateFrequency, presentDate.current]);

  useEffect(() => {
    if (selectedTimeOffset) {
      setFrom(addToDate(new Date(to), -1, selectedTimeOffset));
    } else if (from && changedTimeOffset) {
      setFrom(
        defaultFromDateTime({
          loadConf: props.loadConf,
          referenceTime: props.referenceTime,
        })
      );
    }
  }, [selectedTimeOffset]);

  useEffect(() => {
    if (isOpen) setDateFrom(from);
  }, [from]);

  const addTimeToBothDates = (minutesToAdd: number) => {
    setFrom(oldFrom => addToDate(oldFrom, minutesToAdd, "MINUTES"));
    setTo(oldTo => addToDate(oldTo, minutesToAdd, "MINUTES"));
  };

  const onRefresh = () => {
    if (from && to && props.identifiers) {
      refetch();
    }
  };

  const setFromDateHandler = (date: Date) => {
    if (refresh.turned) {
      setRefresh({ ...refresh, turned: false });
    }
    setChangedTimeOffset(false);
    setFrom(date);
    setSelectedTimeOffset(undefined);
  };

  const setToDateHandler = (date: Date) => {
    if (refresh.turned) {
      setRefresh({ ...refresh, turned: false });
    }
    setChangedTimeOffset(false);
    setTo(date);
    if (isOpen) setDateTo(date);
    setSelectedTimeOffset(undefined);
  };

  const onSelectButtonChange = (e: any) => {
    if (refresh.turned) {
      setRefresh({ ...refresh, turned: false });
    }
    setSelectedTimeOffset(e.value);
    setChangedTimeOffset(true);
  };
  const syncpresentdate = () => {
    presentDate.current = truncateDate(new Date(), "MINUTES");
  };

  const totalDataPoints = () => {
    if (traces) {
      return traces.map((t: any) => t.x?.length).reduce((c: any, a: any) => c! + a!, 0);
    }

    return 0;
  };

  const printRequestingTime = () => {
    if (requestingTime.current.from && requestingTime.current.to) {
      const time = requestingTime.current.to.getTime() - requestingTime.current.from.getTime();
      if (time > 1000) {
        return `${(time / 1000).toFixed(1)}s`;
      }
      return `${time.toFixed(0)}ms`;
    }
    return "-";
  };

  const getChartHeight = () => {
    if (props.height != null) {
      return getChartSizeValue(props.height);
    }
    if (height && height2 && height >= 0 && height2 >= 0 && props.dynamicHeight) {
      return height - height2 - 4;
    }
    return 400;
  };

  const getChartWidth = () => {
    if (props.width != null) {
      return getChartSizeValue(props.width);
    }
    return width;
  };

  if (props.reportMode && !props.width) {
    return <h3 style={{ color: "red" }}>Width property is required</h3>;
  }

  return (
    <div
      ref={ref}
      style={{
        overflow: "hidden",
        height: "100%",
        width: props.reportMode ? props.width : undefined,
      }}
      id={props.uniqueKey}
    >
      {props.dateAndRefreshButtonsVisible !== false && (
        <div
          ref={ref2}
          className="flex align-items-center p-mx-2 justify-content-between non-printable"
        >
          <div className="flex align-items-center p-mx-2 ">
            {props.showDateSelection && (
              <>
                {!props.getMostRecent && (
                  <CalendarWrapper
                    id="from"
                    value={from}
                    showTime={shouldShowTime(props.loadConf)}
                    onChange={setFromDateHandler}
                    className="p-mr-2"
                    debounceTimeInMs={300}
                  />
                )}
                <CalendarWrapper
                  id="to"
                  value={to}
                  showTime={shouldShowTime(props.loadConf)}
                  onChange={setToDateHandler}
                  className="p-mr-2"
                  debounceTimeInMs={300}
                />
                <div className="flex gap-2 align-items-center">
                  <div style={{ flexWrap: "nowrap", whiteSpace: "nowrap" }}>
                    <SelectButton
                      value={selectedTimeOffset}
                      options={offsetOptions}
                      onChange={onSelectButtonChange}
                      className="select-button-class"
                    />
                  </div>

                  {props.datasetChart !== "pie" && (
                    <span className="flex align-items-center">
                      <Button
                        icon="pi pi-info-circle"
                        className="p-button-secondary p-button-text"
                        disabled
                      />
                      {isLoading ? (
                        <span>
                          <ProgressSpinner
                            style={{ width: "2.5rem", height: "2.5rem" }}
                            strokeWidth="4"
                            fill="var(--surface-ground)"
                            animationDuration=".5s"
                          />
                        </span>
                      ) : (
                        <>
                          <Tooltip target=".time-spent-requesting" showDelay={1000} />
                          <span
                            className="time-spent-requesting"
                            data-pr-tooltip={printRequestingTime()}
                          >
                            {totalDataPoints()}
                          </span>
                        </>
                      )}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="flex align-items-center">
            {!props.hideRefresh && (
              <div>
                <Button
                  icon="pi pi-refresh"
                  type="button"
                  loading={isLoading}
                  onClick={onRefresh}
                  className="p-mr-2 p-button-sm"
                />
              </div>
            )}
            {props.updateFrequency && props.updateFrequency > 0 ? (
              <CustomToggleButton
                active={refresh.turned}
                setActive={val => {
                  syncpresentdate();
                  setRefresh({ ...refresh, turned: val });
                }}
              />
            ) : null}
          </div>
        </div>
      )}

      {!loaded ? (
        <div className="flex justify-content-center p-mb-4">
          <ProgressSpinner
            style={{ width: "20rem", height: "20rem" }}
            strokeWidth="3"
            animationDuration="2s"
          />
        </div>
      ) : (
        props.children!({
          width: getChartSizeValue(getChartWidth()),
          height: getChartHeight(),
          data: traces || [],
          axesConfig: props.axesConfig ? props.axesConfig(traces || []) : undefined,
          chartConfig: config as Partial<Plotly.Config>,
          chartDialogOpen: chartDialog,
        })
      )}
      {chartDialog && (
        <Dialog
          visible={chartDialog}
          style={{ width: "min(100vw, 1000px)", height: "600px" }}
          modal={false}
          resizable
          draggable
          maximizable
          onHide={chartDialogHandler}
        >
          <ChartWrapper {...props} dynamicHeight chartDialogOpen />
        </Dialog>
      )}
    </div>
  );
}
